import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Plus } from 'lucide-react';

const TechIcon = ({ name }: { name: string }) => {
  const icons: Record<string, JSX.Element> = {
    React: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <circle cx="16" cy="16" r="3" fill="currentColor" />
        <ellipse cx="16" cy="16" rx="14" ry="5.5" stroke="currentColor" strokeWidth="1.2" />
        <ellipse cx="16" cy="16" rx="14" ry="5.5" stroke="currentColor" strokeWidth="1.2" transform="rotate(60 16 16)" />
        <ellipse cx="16" cy="16" rx="14" ry="5.5" stroke="currentColor" strokeWidth="1.2" transform="rotate(120 16 16)" />
      </svg>
    ),
    'Next.js': (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.2" />
        <path d="M12 10v12M12 10l10.5 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 10v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    TypeScript: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <rect x="2" y="2" width="28" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <text x="16" y="22" textAnchor="middle" fill="currentColor" fontSize="14" fontWeight="bold" fontFamily="system-ui">TS</text>
      </svg>
    ),
    'Node.js': (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M16 3L28 10v12L16 29 4 22V10z" />
        <path d="M16 10v12M10 13l6 3.5L22 13" strokeLinejoin="round" />
      </svg>
    ),
    Supabase: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <path d="M17 29.2c-.6.8-1.9.3-1.9-.7V18h11.3c1.5 0 2.3 1.8 1.3 2.9L17 29.2z" fill="currentColor" opacity="0.4" />
        <path d="M15 2.8c.6-.8 1.9-.3 1.9.7V14H5.6c-1.5 0-2.3-1.8-1.3-2.9L15 2.8z" fill="currentColor" />
      </svg>
    ),
    iyzico: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <rect x="3" y="7" width="26" height="18" rx="3" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="11" cy="16" r="4" stroke="currentColor" strokeWidth="1.3" />
        <circle cx="21" cy="16" r="4" stroke="currentColor" strokeWidth="1.3" />
        <path d="M14 14.5l3 3M14 17.5l3-3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      </svg>
    ),
    PayTR: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <rect x="3" y="6" width="26" height="20" rx="3" stroke="currentColor" strokeWidth="1.4" />
        <path d="M8 13h5a2.5 2.5 0 0 1 0 5H8v-5zM8 18v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 13v9M17 13h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
    AWS: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M4 20c2 2 6 4 12 4s10-2 12-4" strokeLinecap="round" />
        <path d="M6 22c2 1.5 6 3 10 3s8-1.5 10-3" strokeLinecap="round" />
        <path d="M8 10l4 10 4-8 4 8 4-10" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    ),
    Figma: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" strokeWidth="1.4">
        <rect x="9" y="3" width="7" height="8" rx="3.5" />
        <rect x="16" y="3" width="7" height="8" rx="3.5" />
        <rect x="9" y="11" width="7" height="8" rx="3.5" />
        <circle cx="19.5" cy="15" r="4" />
        <path d="M9 19v4.5a3.5 3.5 0 0 0 7 0V19" />
      </svg>
    ),
    Docker: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M3 15c1-1 3-1 4 0 0-2 2-3 3-3s2 1 2 1V9h3v4h3V9h3v4h3c1 0 2 1 2 2v2c5 0 5 4 2 6-2 4-7 6-13 5S2 22 2 18c0-1 0-2 1-3z" />
        <rect x="8" y="14" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" />
        <rect x="12" y="14" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" />
        <rect x="16" y="14" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" />
        <rect x="12" y="10" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" />
        <rect x="16" y="10" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" />
      </svg>
    ),
    GraphQL: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" strokeWidth="1.3">
        <polygon points="16,3 27,9.5 27,22.5 16,29 5,22.5 5,9.5" />
        <circle cx="16" cy="3" r="2" fill="currentColor" />
        <circle cx="27" cy="9.5" r="2" fill="currentColor" />
        <circle cx="27" cy="22.5" r="2" fill="currentColor" />
        <circle cx="16" cy="29" r="2" fill="currentColor" />
        <circle cx="5" cy="22.5" r="2" fill="currentColor" />
        <circle cx="5" cy="9.5" r="2" fill="currentColor" />
      </svg>
    ),
    Stripe: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 lg:w-10 lg:h-10" fill="none">
        <rect x="3" y="5" width="26" height="22" rx="4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M16.5 12c-3-1.2-4 0-4 1.2s1.5 1.8 3.5 2.3 4 1.5 4 3.5-2 3-4.5 3c-2 0-3.5-.7-4.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  };

  return icons[name] || <span className="text-2xl font-bold">{name[0]}</span>;
};

const techItems = [
  'React', 'Next.js', 'TypeScript', 'Node.js', 'Supabase', 'iyzico',
  'PayTR', 'AWS', 'Figma', 'Docker', 'GraphQL', 'Stripe',
];

const TechStackSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="section-y relative overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="section-head text-center"
        >
          <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">
            {t('tech.tag')}
          </p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground">
            {t('tech.title')}
          </h2>
        </motion.div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {techItems.map((name, i) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -8, scale: 1.05, transition: { type: 'spring', stiffness: 300 } }}
              className="flex flex-col items-center justify-center p-6 lg:p-8 rounded-2xl bg-secondary/30 hover:bg-secondary/60 transition-colors duration-500 cursor-default group"
            >
              <div className="text-muted-foreground group-hover:text-foreground transition-colors duration-300 mb-3">
                <TechIcon name={name} />
              </div>
              <span className="font-body text-xs text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                {name}
              </span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col items-center mt-10 gap-3"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-12 bg-border" />
            <Plus size={14} className="text-muted-foreground" />
            <div className="h-px w-12 bg-border" />
          </div>
          <div className="font-body text-sm text-muted-foreground text-center max-w-md space-y-1 leading-relaxed">
            <p>{t('tech.moreLine1')}</p>
            <p>{t('tech.moreLine2')}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechStackSection;
